/** The event logo's budget, as the API's (EventsController::LOGO_BUDGET). */
export const EVENT_LOGO_MAX_BYTES = 1024 * 1024

/** The longest side of an SVG logo once drawn as a PNG: sharp in the header on any screen, small enough to upload. */
export const EVENT_LOGO_RASTER_PIXELS = 960

const SVG_UNUSABLE = 'This SVG can’t be used. Upload a PNG, JPG or WebP.'
const TOO_LARGE = 'This logo is over 1 MB. Upload a smaller one.'

/** A chosen logo that can't be uploaded; its message says what to upload instead. */
export class EventLogoError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'EventLogoError'
  }
}

const isSvg = (file: File) => file.type === 'image/svg+xml' || /\.svg$/i.test(file.name)

/**
 * The file to upload for a chosen event logo. An SVG is drawn as a transparent PNG first, its longest side
 * `EVENT_LOGO_RASTER_PIXELS`: the API stores and serves SVG as a download, which an `<img>` won't draw, so an SVG logo
 * would show as a broken picture in the event page's header and on the camera joining page. Other images go as chosen.
 * Rejects with EventLogoError when an SVG can't be drawn, or the file is over the 1 MB budget (after drawing too).
 */
export async function prepareEventLogo(file: File): Promise<File> {
  const upload = isSvg(file) ? await svgAsPng(file) : file
  if (upload.size > EVENT_LOGO_MAX_BYTES) throw new EventLogoError(TOO_LARGE)
  return upload
}

async function svgAsPng(file: File): Promise<File> {
  const drawing = await sizedSvg(file)
  const url = URL.createObjectURL(new Blob([drawing.text], { type: 'image/svg+xml' }))
  try {
    const image = new Image()
    image.src = url
    await image.decode()
    const canvas = document.createElement('canvas')
    canvas.width = drawing.width
    canvas.height = drawing.height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('No 2D canvas')
    context.drawImage(image, 0, 0, drawing.width, drawing.height)
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!png) throw new Error('The canvas made no PNG')
    return new File([png], `${file.name.replace(/\.svg$/i, '')}.png`, { type: 'image/png' })
  } catch {
    throw new EventLogoError(SVG_UNUSABLE)
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** The file's text, read the long way: `Blob.text()` is missing from older Safari (and jsdom). */
function readText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })
}

/** A plain length in px (or unitless), or null: a percentage or other unit gives an SVG no size of its own. */
function pixels(value: string | null) {
  const match = value?.trim().match(/^(\d*\.?\d+)(px)?$/)
  return match ? Number(match[1]) : null
}

/**
 * The SVG's text set to draw at the raster size, keeping its shape: its own width and height, else its viewBox's. Its
 * viewBox is kept (or made from its size), so it scales to fill that size.
 */
async function sizedSvg(file: File): Promise<{ text: string; width: number; height: number }> {
  const parsed = new DOMParser().parseFromString(await readText(file), 'image/svg+xml')
  const svg = parsed.documentElement
  if (svg.nodeName !== 'svg' || parsed.getElementsByTagName('parsererror').length > 0) throw new EventLogoError(SVG_UNUSABLE)
  const box = svg.getAttribute('viewBox')?.trim().split(/[\s,]+/).map(Number)
  const viewBox = box?.length === 4 && box[2] > 0 && box[3] > 0 ? { width: box[2], height: box[3] } : null
  const own = { width: pixels(svg.getAttribute('width')), height: pixels(svg.getAttribute('height')) }
  const shape = own.width && own.height ? { width: own.width, height: own.height } : viewBox
  if (!shape) throw new EventLogoError(SVG_UNUSABLE)
  const scale = EVENT_LOGO_RASTER_PIXELS / Math.max(shape.width, shape.height)
  const width = Math.max(1, Math.round(shape.width * scale))
  const height = Math.max(1, Math.round(shape.height * scale))
  if (!viewBox) svg.setAttribute('viewBox', `0 0 ${shape.width} ${shape.height}`)
  svg.setAttribute('width', String(width))
  svg.setAttribute('height', String(height))
  return { text: new XMLSerializer().serializeToString(svg), width, height }
}
